import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, Loader2, AlertTriangle, FileBarChart } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const MONTHS = {
  nl: ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};

export function DeviceReports({ deviceId, deviceName }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [reports, setReports] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState("");

  const now = new Date();
  const [genMonth, setGenMonth] = useState(String(now.getMonth() + 1));
  const [genYear, setGenYear] = useState(String(now.getFullYear()));

  const load = useCallback(() => {
    api.get(`/devices/${deviceId}/reports`).then((r) => setReports(r.data)).catch(() => setReports([]));
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/devices/${deviceId}/reports/generate`, { year: Number(genYear), month: Number(genMonth) });
      toast.success(t("Rapport gegenereerd", "Report generated"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Genereren mislukt", "Generation failed"));
    } finally { setGenerating(false); }
  };

  const download = async (report, dlLang) => {
    setDownloading(`${report.id}-${dlLang}`);
    try {
      const res = await api.get(`/reports/${report.id}/download`, { params: { lang: dlLang }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `THRM-rapport-${deviceName}-${report.year}-${String(report.month).padStart(2, "0")}-${dlLang}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("Download mislukt", "Download failed"));
    } finally { setDownloading(""); }
  };

  const years = [now.getFullYear(), now.getFullYear() - 1].map(String);

  return (
    <div className="rounded-lg border border-border bg-card" data-testid="device-reports">
      <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight">
          <FileBarChart className="h-4 w-4 text-primary" /> {t("Maandrapporten", "Monthly reports")}
        </h2>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            <Select value={genMonth} onValueChange={setGenMonth}>
              <SelectTrigger className="h-8 w-32" data-testid="report-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS[lang].map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={genYear} onValueChange={setGenYear}>
              <SelectTrigger className="h-8 w-24" data-testid="report-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" className="h-8 gap-1.5" onClick={generate} disabled={generating} data-testid="generate-report-btn">
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              {t("Genereer", "Generate")}
            </Button>
          </div>
        )}
      </div>

      {reports === null ? (
        <div className="p-5 text-sm text-muted-foreground">{t("Laden…", "Loading…")}</div>
      ) : reports.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileText className="h-9 w-9 text-muted-foreground/40" />
          <p className="mt-3 text-sm text-muted-foreground">{t("Nog geen rapporten. Een beheerder kan hierboven een gecontroleerd maandrapport genereren.", "No reports yet. An administrator can generate a verified monthly report above.")}</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {reports.map((r) => (
            <div key={r.id} className="flex flex-col gap-3 px-5 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`report-row-${r.id}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary"><FileText className="h-4 w-4" /></div>
                <div>
                  <div className="text-sm font-semibold">{MONTHS[lang][r.month - 1].charAt(0).toUpperCase() + MONTHS[lang][r.month - 1].slice(1)} {r.year}</div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span>{r.total_measurements} {t("metingen", "measurements")}</span>
                    {r.crit_count > 0 && <span className="flex items-center gap-1 text-red-500"><AlertTriangle className="h-3 w-3" /> {r.crit_count} {t("kritiek", "critical")}</span>}
                    {r.warn_count > 0 && <span className="text-amber-500">{r.warn_count} {t("waarschuwingen", "warnings")}</span>}
                    {r.crit_count === 0 && r.warn_count === 0 && <span className="text-emerald-500">{t("geen uitzonderingen", "no exceptions")}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => download(r, "nl")} disabled={downloading === `${r.id}-nl`} data-testid={`download-nl-${r.id}`}>
                  {downloading === `${r.id}-nl` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} NL
                </Button>
                <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => download(r, "en")} disabled={downloading === `${r.id}-en`} data-testid={`download-en-${r.id}`}>
                  {downloading === `${r.id}-en` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} EN
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
